import { z } from 'zod';

// Page Template schemas
export const pageTemplateSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  siteId: z.string().uuid().nullable(),
  name: z.string().min(1, 'Template name is required'),
  description: z.string().optional(),
  tags: z.string().optional(),
  isPublic: z.boolean().default(false),
  usageCount: z.number().int().min(0).default(0),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  pageData: z.object({
    title: z.string(),
    slug: z.string(),
    components: z.string().nullable(),
  }),
});

export const createPageTemplateSchema = z.object({
  pageId: z.string().uuid('Invalid page ID'),
  name: z.string().min(1, 'Template name is required'),
  description: z.string().optional(),
  tags: z.string().optional(),
  siteId: z.string().uuid().optional(),
  isPublic: z.boolean().default(false),
});

export const updatePageTemplateSchema = z.object({
  name: z.string().min(1, 'Template name is required').optional(),
  description: z.string().optional(),
  tags: z.string().optional(),
  isPublic: z.boolean().optional(),
});

export const searchPageTemplatesSchema = z.object({
  search: z.string().optional(),
  tags: z.string().optional(),
  siteId: z.string().uuid().optional(),
  includePublic: z.boolean().optional().default(true),
});

export const createPageFromTemplateSchema = z.object({
  templateId: z.string().uuid(),
  title: z.string().min(1, 'Page title is required'),
  slug: z.string().min(1, 'Page slug is required'),
  orderPosition: z.number().int().min(0).optional(),
  isHomePage: z.boolean().optional(),
  noindex: z.boolean().optional(), // Whether to exclude from search engine indexing
});

// Page Template API Response schemas
export const pageTemplateBrowseResponseSchema = z.object({
  templates: z.array(pageTemplateSchema),
  total: z.number().int().min(0),
});

export const createPageFromTemplateResponseSchema = z.object({
  id: z.string().uuid(),
  siteId: z.string().uuid(),
  title: z.string(),
  slug: z.string(),
  components: z.string().nullable(),
  orderPosition: z.number().int(),
  showLayout: z.boolean().default(true),
  isHomePage: z.boolean().optional(),
  noindex: z.boolean().optional(),
  layoutId: z.string().uuid().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  sourceTemplate: z.object({
    id: z.string().uuid(),
    name: z.string(),
    usageCount: z.number().int(),
  }),
});

// Site Template Layout schema
const templateLayoutSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  components: z.union([z.string(), z.array(z.any())]).nullable(),
  isDefault: z.boolean(),
});

// Site Template Page schema
const templatePageSchema = z.object({
  title: z.string(),
  slug: z.string(),
  components: z.union([z.string(), z.array(z.any())]).nullable(),
  orderPosition: z.number().int(),
  isHomePage: z.boolean().optional(),
  noindex: z.boolean().optional(),
  layoutId: z.string().nullable().optional(),
  showLayout: z.boolean().optional(),
});

// Site Template schemas
export const siteTemplateSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  name: z.string().min(1, 'Template name is required'),
  description: z.string().optional(),
  tags: z.string().optional(),
  isPublic: z.boolean().default(false),
  isSystem: z.boolean().default(false),
  usageCount: z.number().int().min(0).default(0),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  siteData: z.object({
    name: z.string(),
    theme: z.string().optional(),
    brandPackage: z
      .object({
        name: z.string(),
        description: z.string().optional(),
        colors: z.any(),
        fonts: z.any(),
      })
      .optional(),
    layouts: z.array(templateLayoutSchema).optional(),
    pages: z.array(templatePageSchema),
    metadata: z
      .object({
        originalSiteId: z.string(),
        createdFromSite: z.boolean(),
        totalPages: z.number().int(),
        totalLayouts: z.number().int().optional(),
      })
      .optional(),
  }),
});

export const createSiteTemplateSchema = z.object({
  name: z.string().min(1, 'Template name is required'),
  description: z.string().optional(),
  tags: z.string().optional(),
  isPublic: z.boolean().default(false),
  isSystem: z.boolean().default(false),
});

export const updateSiteTemplateSchema = z.object({
  name: z.string().min(1, 'Template name is required').optional(),
  description: z.string().optional(),
  tags: z.string().optional(),
  isPublic: z.boolean().optional(),
  isSystem: z.boolean().optional(),
});

export const searchSiteTemplatesSchema = z.object({
  search: z.string().optional(),
  tags: z.string().optional(),
  includePublic: z.boolean().optional().default(true),
});

export const createSiteFromTemplateSchema = z.object({
  templateId: z.string().uuid(),
  name: z.string().min(1, 'Site name is required'),
});

// Site Template API Response schemas
export const siteTemplateBrowseResponseSchema = z.object({
  templates: z.array(siteTemplateSchema),
  total: z.number().int().min(0),
});

export const createSiteFromTemplateResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  pages: z.array(
    z.object({
      id: z.string().uuid(),
      siteId: z.string().uuid(),
      title: z.string(),
      slug: z.string(),
      components: z.string().nullable(),
      orderPosition: z.number().int(),
      createdAt: z.string().datetime(),
      updatedAt: z.string().datetime(),
    })
  ),
  sourceTemplate: z.object({
    id: z.string().uuid(),
    name: z.string(),
    usageCount: z.number().int(),
  }),
});

// Page Template type exports
export type PageTemplate = z.infer<typeof pageTemplateSchema>;
export type CreatePageTemplateRequest = z.infer<typeof createPageTemplateSchema>;
export type UpdatePageTemplateRequest = z.infer<typeof updatePageTemplateSchema>;
export type SearchPageTemplatesRequest = z.infer<typeof searchPageTemplatesSchema>;
export type CreatePageFromTemplateRequest = z.infer<typeof createPageFromTemplateSchema>;
export type PageTemplateBrowseResponse = z.infer<typeof pageTemplateBrowseResponseSchema>;
export type CreatePageFromTemplateResponse = z.infer<typeof createPageFromTemplateResponseSchema>;

// Site Template type exports
export type SiteTemplate = z.infer<typeof siteTemplateSchema>;
export type CreateSiteTemplateRequest = z.infer<typeof createSiteTemplateSchema>;
export type UpdateSiteTemplateRequest = z.infer<typeof updateSiteTemplateSchema>;
export type SearchSiteTemplatesRequest = z.infer<typeof searchSiteTemplatesSchema>;
export type CreateSiteFromTemplateRequest = z.infer<typeof createSiteFromTemplateSchema>;
export type SiteTemplateBrowseResponse = z.infer<typeof siteTemplateBrowseResponseSchema>;
export type CreateSiteFromTemplateResponse = z.infer<typeof createSiteFromTemplateResponseSchema>;
